import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Waves, Play, Square } from "lucide-react";

const ACCENT = "#2DD4BF";
const DURATIONS = [
  { label: "1 min · reset", secs: 60 },
  { label: "3 min · calm", secs: 180 },
  { label: "5 min · focus", secs: 300 },
  { label: "10 min · deep", secs: 600 },
];
// coherent breath: inhale 5s, exhale 5s
const IN = 5, OUT = 5, CYCLE = IN + OUT;

export const CalmCurrent = () => {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [secs, setSecs] = useState(180);
  const [left, setLeft] = useState(0);
  const [reflection, setReflection] = useState(null);
  const stateRef = useRef({ running: false });
  const startRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let t0 = performance.now();
    const render = (now) => {
      const w = canvas.width = canvas.offsetWidth;
      const h = canvas.height = canvas.offsetHeight;
      const elapsed = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);
      // flowing current — layered sine waves
      for (let layer = 0; layer < 3; layer++) {
        ctx.beginPath();
        const amp = 18 + layer * 10;
        const yBase = h * (0.5 + (layer - 1) * 0.12);
        for (let x = 0; x <= w; x += 6) {
          const y = yBase + Math.sin(x / 90 + elapsed * (0.6 + layer * 0.2)) * amp;
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(45,212,191,${0.18 - layer * 0.04})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      // breathing orb
      const st = stateRef.current;
      let phaseLabel = "Breathe";
      let scale = 1;
      if (st.running) {
        const inCycle = ((now - startRef.current) / 1000) % CYCLE;
        if (inCycle < IN) { scale = 1 + (inCycle / IN) * 0.6; phaseLabel = "Inhale"; }
        else { scale = 1.6 - ((inCycle - IN) / OUT) * 0.6; phaseLabel = "Exhale"; }
      }
      const r = 60 * scale;
      const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, r);
      g.addColorStop(0, "rgba(45,212,191,0.55)");
      g.addColorStop(1, "rgba(45,212,191,0.05)");
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(226,232,240,0.9)";
      ctx.font = "600 16px system-ui";
      ctx.textAlign = "center";
      ctx.fillText(st.running ? phaseLabel : "Ready", w / 2, h / 2 + 5);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const finish = async (completed) => {
    stateRef.current.running = false;
    setRunning(false);
    if (completed) {
      try {
        const { data } = await api.post("/games/calm/complete", { duration_seconds: secs });
        setReflection(data.reflection);
        toast.success(`Practice complete · +${data.xp_gained} XP`);
      } catch (e) { /* noop */ }
    }
  };

  const start = () => {
    setReflection(null);
    startRef.current = performance.now();
    stateRef.current.running = true;
    setRunning(true);
    setLeft(secs);
    const tick = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) { clearInterval(tick); finish(true); return 0; }
        return l - 1;
      });
    }, 1000);
    stateRef.current.tick = tick;
  };

  const stop = () => { clearInterval(stateRef.current.tick); finish(false); };

  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");

  return (
    <div data-testid="calm-current">
      <div className="flex items-center gap-2 mb-4 text-sm" style={{ color: ACCENT }}><Waves className="w-4 h-4" /> Sync your breath with the current</div>
      <div className="relative rounded-2xl overflow-hidden border border-teal-300/20 mb-5" style={{ height: 300, background: "linear-gradient(180deg,#04121a,#071016)" }}>
        <canvas ref={canvasRef} className="w-full h-full" />
        {running && <div className="absolute top-3 right-4 text-teal-200 font-mono text-lg" data-testid="calm-timer">{mm}:{ss}</div>}
      </div>

      {reflection && (
        <div className="rounded-xl border border-teal-300/30 bg-teal-300/5 p-4 mb-5" data-testid="calm-reflection">
          <p className="text-sm text-teal-100/90">{reflection}</p>
        </div>
      )}

      {!running ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {DURATIONS.map((d) => (
              <button key={d.secs} data-testid={`calm-dur-${d.secs}`} onClick={() => setSecs(d.secs)}
                className={`rounded-xl border px-3 py-2.5 text-xs transition-colors duration-300 ${secs === d.secs ? "text-slate-900" : "text-slate-300 border-white/10 hover:border-white/30"}`}
                style={secs === d.secs ? { background: ACCENT, borderColor: ACCENT } : {}}>
                {d.label}
              </button>
            ))}
          </div>
          <button data-testid="calm-start-btn" onClick={start} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900" style={{ background: ACCENT }}>
            <Play className="w-4 h-4" /> Begin practice
          </button>
          <p className="text-xs text-slate-500 mt-3">If your attention wanders, gently return to the breath. There&rsquo;s no failing here.</p>
        </>
      ) : (
        <button data-testid="calm-stop-btn" onClick={stop} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm text-slate-200 hover:border-white/40 transition-colors duration-300">
          <Square className="w-4 h-4" /> End early
        </button>
      )}
    </div>
  );
};
